import React, { useEffect, useMemo, useState } from "react";
import { AlertTriangle, ChevronDown, ChevronUp, FileText, Minus, Plus } from "react-feather";
import { Modal } from "../../../design-system/components/composite/Modal/Modal";
import {
  ConfirmInput,
  FooterButton,
  ImpactContent,
  ImpactPanel,
  ImpactToggle,
  ModalBody,
  ModalDescription,
  ModalFooter,
  ModalTitle,
  StoryBadge,
  StoryChildren,
  StoryRow,
  StoryTree,
  WarningBox,
  WarningContent,
} from "./DeleteModal.styles";
import { DeleteModalProps } from "./DeleteModal.types";

const DEFAULT_AFFECTED_STORIES = [
  {
    id: "drug-launch",
    label: "Drug X Launch Performance Analysis",
    children: ["Revenue performance vs target", "Market share trend"],
  },
  {
    id: "sales-deep-dive",
    label: "Sales deep dive",
  },
  {
    id: "customer-growth",
    label: "Customer growth overview",
  },
];

export const DeleteModal = ({
  open,
  isLoading = false,
  affectedStories,
  onConfirm,
  onCancel,
}: DeleteModalProps) => {
  const [step, setStep] = useState<"impact" | "confirm">("impact");
  const [impactExpanded, setImpactExpanded] = useState(false);
  const [confirmationInput, setConfirmationInput] = useState("");

  const storyList = useMemo(
    () => (affectedStories && affectedStories.length > 0 ? affectedStories : DEFAULT_AFFECTED_STORIES),
    [affectedStories],
  );
  const canDelete = confirmationInput.trim() === "DELETE";

  useEffect(() => {
    if (open) {
      setStep("impact");
      setImpactExpanded(false);
      setConfirmationInput("");
    }
  }, [open]);

  return (
    <Modal
      open={open}
      onClose={onCancel}
      size="md"
      closeOnBackdropClick={!isLoading}
      closeOnEscape={!isLoading}
      closable={!isLoading}
    >
      <ModalBody>
        <ModalTitle>
          {step === "impact" ? "Delete integration?" : "Confirm deletion?"}
        </ModalTitle>

        {step === "impact" ? (
          <>
            <ModalDescription>
              This will permanently remove this integration and its configuration from MagicBI.
            </ModalDescription>

            <WarningBox>
              <AlertTriangle size={16} color="#FB975B" />
              <WarningContent>
                <ul>
                  <li>Data from connected stories will be removed</li>
                  <li>Linked reports in those stories will no longer be available</li>
                  <li>All schedules will be deleted</li>
                  <li>You&apos;ll need to reconnect to use this source again</li>
                  <li>This action cannot be undone</li>
                </ul>
              </WarningContent>
            </WarningBox>

            <ImpactPanel>
              <ImpactToggle
                type="button"
                onClick={() => setImpactExpanded((previousValue) => !previousValue)}
                aria-expanded={impactExpanded}
              >
                <span>Affected stories and reports</span>
                {impactExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
              </ImpactToggle>

              {impactExpanded ? (
                <ImpactContent>
                  <StoryTree>
                    {storyList.map((story) => (
                      <div key={story.id}>
                        <StoryRow>
                          {story.children && story.children.length > 0 ? (
                            <Minus size={14} />
                          ) : (
                            <Plus size={14} />
                          )}
                          <StoryBadge>{story.label.slice(0, 2).toUpperCase()}</StoryBadge>
                          <span>{story.label}</span>
                        </StoryRow>

                        {story.children && story.children.length > 0 ? (
                          <StoryChildren>
                            {story.children.map((childLabel) => (
                              <StoryRow key={childLabel}>
                                <FileText size={14} />
                                <span>{childLabel}</span>
                              </StoryRow>
                            ))}
                          </StoryChildren>
                        ) : null}
                      </div>
                    ))}
                  </StoryTree>
                </ImpactContent>
              ) : null}
            </ImpactPanel>
          </>
        ) : (
          <>
            <ModalDescription>
              This action will permanently delete the integration, connected schedules, and linked story data from MagicBI.
              <br />
              To confirm, type <strong>DELETE</strong> below.
            </ModalDescription>
            <ConfirmInput
              type="text"
              value={confirmationInput}
              placeholder="Type DELETE to confirm"
              onChange={(event) => setConfirmationInput(event.target.value)}
            />
          </>
        )}
      </ModalBody>

      <ModalFooter>
        <FooterButton type="button" onClick={onCancel} disabled={isLoading}>
          Cancel
        </FooterButton>
        {step === "impact" ? (
          <FooterButton
            type="button"
            $tone="danger"
            onClick={() => setStep("confirm")}
            disabled={isLoading}
          >
            Delete
          </FooterButton>
        ) : (
          <FooterButton
            type="button"
            $tone="danger"
            onClick={onConfirm}
            disabled={isLoading || !canDelete}
          >
            Delete
          </FooterButton>
        )}
      </ModalFooter>
    </Modal>
  );
};
